import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n, AVAILABLE_LANGUAGES } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import Flag from '../components/Flag';
import { ROLE_LABELS, ROLE_COLORS, isAdmin, isAgent, ROLES } from '../constants/roles';
import AppHeader from '../components/AppHeader';
import Button from '../components/ui/Button';
import SavedIndicator from '../components/ui/SavedIndicator';
import '../styles/ProfilePage.css';
import '../styles/SessionsSection.css';

const RoleBadge = ({ role }) => {
  return (
    <span className={`profile-role-badge role-badge--${role}`}>
      {ROLE_LABELS[role] ?? `Role ${role}`}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString([], {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

// ---- Password strength validator (same rule as backend) ----
const isStrongPassword = (pw) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(pw);

// ============================================================
// PASSWORD CHANGE CARD
// ============================================================
const PasswordChangeCard = ({ userId, isSelf, isAdminUser, t, api }) => {
  const [open, setOpen]               = useState(false);
  const [currentPw, setCurrentPw]     = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState(null);   // { type: 'success'|'error', text }
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const reset = () => {
    setCurrentPw(''); setNewPw(''); setConfirmPw('');
    setMsg(null); setSaving(false);
  };

  const handleToggle = () => {
    setOpen(o => !o);
    reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!isStrongPassword(newPw)) {
      setMsg({ type: 'error', text: t('admin.passwordWeak') });
      return;
    }
    if (newPw !== confirmPw) {
      setMsg({ type: 'error', text: t('admin.passwordMismatch') });
      return;
    }

    setSaving(true);
    try {
      const body = { newPassword: newPw };
      if (isSelf) body.currentPassword = currentPw;
      await api.put(`/admin/users/${userId}/password`, body);
      setMsg({ type: 'success', text: t('admin.passwordChanged') });
      reset();
      setOpen(false);
    } catch (err) {
      const errMsg = err.response?.data?.error || '';
      if (errMsg.toLowerCase().includes('current') || errMsg.toLowerCase().includes('incorrect')) {
        setMsg({ type: 'error', text: t('admin.currentPasswordWrong') });
      } else if (errMsg.toLowerCase().includes('weak')) {
        setMsg({ type: 'error', text: t('admin.passwordWeak') });
      } else {
        setMsg({ type: 'error', text: t('admin.passwordChangeFailed') });
      }
    } finally {
      setSaving(false);
    }
  };

  // Csak admin vagy saját profil láthatja
  if (!isSelf && !isAdminUser) return null;

  const pwStrength = newPw.length === 0 ? null : isStrongPassword(newPw) ? 'strong' : 'weak';
  const matchOk    = confirmPw.length === 0 ? null : newPw === confirmPw;

  return (
    <div className="pw-change-card">
      <button className="pw-change-toggle" onClick={handleToggle}>
        🔑 {t('admin.changePassword')}
        <span className={`pw-toggle-arrow ${open ? 'open' : ''}`}>▼</span>
      </button>

      {msg && (
        <div className={`pw-msg pw-msg--${msg.type}`}>
          {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
        </div>
      )}

      {open && (
        <form className="pw-change-form" onSubmit={handleSubmit}>

          {/* Saját profil esetén kell a régi jelszó */}
          {isSelf && (
            <div className="pw-field-group">
              <label>{t('admin.currentPassword')}</label>
              <div className="pw-input-wrap">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pw-input"
                />
                <button type="button" className="pw-eye" onClick={() => setShowCurrent(s => !s)}>
                  {showCurrent ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          )}

          <div className="pw-field-group">
            <label>{t('password.newPassword')}</label>
            <div className="pw-input-wrap">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                required
                autoComplete="new-password"
                className={`pw-input ${pwStrength === 'weak' ? 'pw-input--weak' : pwStrength === 'strong' ? 'pw-input--strong' : ''}`}
              />
              <button type="button" className="pw-eye" onClick={() => setShowNew(s => !s)}>
                {showNew ? '🙈' : '👁️'}
              </button>
            </div>
            {pwStrength === 'weak' && (
              <span className="pw-hint pw-hint--error">⚠️ {t('admin.passwordWeak')}</span>
            )}
            {pwStrength === null && (
              <span className="pw-hint">{t('admin.passwordHint')}</span>
            )}
          </div>

          <div className="pw-field-group">
            <label>{t('password.confirmNewPassword')}</label>
            <div className="pw-input-wrap">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                required
                autoComplete="new-password"
                className={`pw-input ${matchOk === false ? 'pw-input--weak' : matchOk === true ? 'pw-input--strong' : ''}`}
              />
              <button type="button" className="pw-eye" onClick={() => setShowConfirm(s => !s)}>
                {showConfirm ? '🙈' : '👁️'}
              </button>
            </div>
            {matchOk === false && (
              <span className="pw-hint pw-hint--error">⚠️ {t('admin.passwordMismatch')}</span>
            )}
          </div>

          <div className="pw-form-actions">
            <Button type="button" variant="ghost" onClick={handleToggle} disabled={saving}>
              {t('admin.cancel')}
            </Button>
            <Button type="submit" variant="solid"
              disabled={saving || pwStrength !== 'strong' || matchOk !== true || (isSelf && !currentPw)}>
              {saving ? t('admin.saving') : `🔑 ${t('admin.setNewPassword')}`}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

// ============================================================
// PREFERRED LANGUAGE CARD — csak saját profilon
// ============================================================
const ThemeCard = ({ t, api, initial }) => {
  const { theme, setTheme } = useTheme();
  const [status, setStatus] = useState(null); // null | 'saved'

  const handleChange = async (newTheme) => {
    setStatus('saving');
    await setTheme(newTheme, true);
    setStatus('saved');
  };

  const themes = [
    { value: 'dark',  label: t('profile.themeDark'),  icon: '🌑' },
    { value: 'light', label: t('profile.themeLight'), icon: '☀️' },
  ];

  return (
    <div className="profile-card">
      <h3 className="card-section-title">{t('profile.themeTitle')}</h3>
      <div className="theme-options">
        {themes.map(opt => (
          <button
            key={opt.value}
            className={`theme-option-btn${theme === opt.value ? ' active' : ''}`}
            onClick={() => handleChange(opt.value)}
            disabled={status === 'saving'}
          >
            <span className="theme-option-icon">{opt.icon}</span>
            <span>{opt.label}</span>
          </button>
        ))}
      </div>
      <SavedIndicator status={status} />
    </div>
  );
};

const PreferredLanguageCard = ({ t, api, initial }) => {
  const { setLanguage } = useI18n();
  const [selected, setSelected] = useState(initial || '');
  const [status, setStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const handleChange = async (e) => {
    const lang = e.target.value;
    setSelected(lang);
    setStatus('saving');
    try {
      await api.put('/auth/me/language', { language: lang });
      setLanguage(lang);
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="pw-change-card">
      <div className="lang-pref-row">
        <span className="lang-pref-label">
          🌐 {t('profile.preferredLanguage')}
        </span>
        <span className="lang-pref-flag">
          {selected && <Flag code={selected} />}
        </span>
        <select
          className="lang-pref-select"
          value={selected}
          onChange={handleChange}
          disabled={status === 'saving'}
        >
          <option value="" disabled>—</option>
          {AVAILABLE_LANGUAGES.map(l => (
            <option key={l.code} value={l.code}>{l.name}</option>
          ))}
        </select>
      </div>
      <SavedIndicator status={status} />
    </div>
  );
};

// ============================================================
// CASE FORDÍTÁSI NYELVEK — csak munkatársaknak (agent/admin/superadmin)
// ============================================================
const TRANSLATION_TARGETS = [
  { code: 'en', name: 'English' },
  { code: 'hu', name: 'Magyar' },
  { code: 'es', name: 'Español' },
];

const CaseTranslationLangsCard = ({ t, api, initial }) => {
  const init = (initial || '').split(',').map(s => s.trim()).filter(Boolean);
  const [selected, setSelected] = useState(init.length ? init : ['en']);
  const [status, setStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const toggle = async (code) => {
    const next = selected.includes(code)
      ? selected.filter(c => c !== code)
      : [...selected, code];
    setSelected(next);
    setStatus('saving');
    try {
      await api.put('/auth/me/translation-langs', { langs: next });
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="pw-change-card">
      <span className="lang-pref-label">🔁 {t('profile.translationLangsTitle')}</span>
      <p className="card-hint">{t('profile.translationLangsHint')}</p>
      <div className="translation-langs-row">
        {TRANSLATION_TARGETS.map(l => (
          <label key={l.code} className={`translation-lang-chip ${selected.includes(l.code) ? 'active' : ''}`}>
            <input
              type="checkbox"
              checked={selected.includes(l.code)}
              onChange={() => toggle(l.code)}
              disabled={status === 'saving'}
            />
            <Flag code={l.code} /> <span>{l.name}</span>
          </label>
        ))}
      </div>
      <SavedIndicator status={status} />
    </div>
  );
};

// ============================================================
// 2FA (TOTP) CARD — csak saját profilon
// ============================================================
const TwoFactorCard = ({ t, api }) => {
  const [open, setOpen]               = useState(false);
  const [enabled, setEnabled]         = useState(null);   // null = töltés alatt
  const [backupRemaining, setBackupRemaining] = useState(0);
  const [msg, setMsg]                 = useState(null);

  // Setup flow state
  const [qr, setQr]                   = useState(null);
  const [secret, setSecret]           = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy]               = useState(false);

  // Disable flow state
  const [disablePw, setDisablePw]     = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/auth/2fa/status');
      setEnabled(r.data.enabled);
      setBackupRemaining(r.data.backupCodesRemaining);
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleStartSetup = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post('/auth/2fa/setup');
      setQr(r.data.qr);
      setSecret(r.data.secret);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || t('twofa.setupFailed') });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = await api.post('/auth/2fa/confirm', { code: confirmCode.trim() });
      setBackupCodes(r.data.backupCodes);
      setQr(null); setSecret(null); setConfirmCode('');
      setEnabled(true);
      setBackupRemaining(r.data.backupCodes.length);
      setMsg({ type: 'success', text: t('twofa.enabled') });
    } catch (e2) {
      setMsg({ type: 'error', text: e2.response?.data?.error || t('twofa.invalidCode') });
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api.post('/auth/2fa/disable', { password: disablePw });
      setEnabled(false);
      setShowDisable(false); setDisablePw('');
      setBackupCodes(null);
      setMsg({ type: 'success', text: t('twofa.disabled') });
    } catch (e2) {
      setMsg({ type: 'error', text: e2.response?.data?.error || t('twofa.disableFailed') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pw-change-card">
      <button className="pw-change-toggle" onClick={() => { setOpen(o => !o); setMsg(null); }}>
        🛡️ {t('twofa.title')}
        {enabled !== null && (
          <span className={`twofa-badge ${enabled ? 'twofa-badge--on' : 'twofa-badge--off'}`}>
            {enabled ? t('twofa.statusOn') : t('twofa.statusOff')}
          </span>
        )}
        <span className={`pw-toggle-arrow ${open ? 'open' : ''}`}>▼</span>
      </button>

      {msg && (
        <div className={`pw-msg pw-msg--${msg.type}`}>
          {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
        </div>
      )}

      {open && (
        <div className="twofa-body">

          {/* Backup kódok megjelenítése (csak közvetlenül aktiválás után) */}
          {backupCodes && (
            <div className="twofa-backup-box">
              <strong>{t('twofa.backupTitle')}</strong>
              <p className="twofa-backup-hint">{t('twofa.backupHint')}</p>
              <div className="twofa-backup-grid">
                {backupCodes.map(c => <code key={c}>{c}</code>)}
              </div>
            </div>
          )}

          {/* KIKAPCSOLT állapot — setup indítása */}
          {enabled === false && !qr && (
            <>
              <p className="twofa-desc">{t('twofa.description')}</p>
              <Button variant="solid" onClick={handleStartSetup} disabled={busy}>
                {busy ? '…' : `🛡️ ${t('twofa.enable')}`}
              </Button>
            </>
          )}

          {/* SETUP folyamatban — QR + megerősítő kód */}
          {qr && (
            <form onSubmit={handleConfirm} className="twofa-setup">
              <p className="twofa-desc">{t('twofa.scanQr')}</p>
              <img src={qr} alt="TOTP QR" className="twofa-qr" />
              <p className="twofa-secret">
                {t('twofa.manualEntry')}: <code>{secret}</code>
              </p>
              <div className="pw-field-group">
                <label>{t('twofa.confirmCode')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={confirmCode}
                  onChange={e => setConfirmCode(e.target.value)}
                  placeholder="123456"
                  required
                  className="pw-input"
                />
              </div>
              <Button type="submit" variant="solid" disabled={busy || !confirmCode.trim()}>
                {busy ? '…' : `✅ ${t('twofa.activate')}`}
              </Button>
            </form>
          )}

          {/* BEKAPCSOLT állapot — kikapcsolás */}
          {enabled === true && (
            <>
              <p className="twofa-desc">
                {t('twofa.activeInfo')} ({t('twofa.backupRemaining')}: {backupRemaining})
              </p>
              {!showDisable ? (
                <Button variant="ghost" onClick={() => setShowDisable(true)}>
                  {t('twofa.disable')}
                </Button>
              ) : (
                <form onSubmit={handleDisable} className="twofa-setup">
                  <div className="pw-field-group">
                    <label>{t('twofa.disableConfirm')}</label>
                    <input
                      type="password"
                      value={disablePw}
                      onChange={e => setDisablePw(e.target.value)}
                      autoComplete="current-password"
                      required
                      className="pw-input"
                    />
                  </div>
                  <div className="pw-form-actions">
                    <Button type="button" variant="ghost"
                            onClick={() => { setShowDisable(false); setDisablePw(''); }}>
                      {t('admin.cancel')}
                    </Button>
                    <Button type="submit" variant="solid" disabled={busy || !disablePw}>
                      {busy ? '…' : t('twofa.disable')}
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Klikkre vágólapra másoló chip (botnév, /start parancs)
const CopyText = ({ value, children, title }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard nem elérhető — némán */ }
  };
  return (
    <button type="button" className={`copy-chip${copied ? ' copy-chip--copied' : ''}`} onClick={copy} title={title}>
      <span className="copy-chip-text">{children ?? value}</span>
      <span className="copy-chip-icon">{copied ? '✓' : '⧉'}</span>
    </button>
  );
};

// ============================================================
// TELEGRAM CARD — saját profil, csak staffnak
// ============================================================
const TelegramCard = ({ t, api }) => {
  const [connected, setConnected] = useState(null); // null = töltés alatt
  const [linkInfo, setLinkInfo]   = useState(null);  // { code, deep_link }
  const [botUsername, setBotUsername] = useState('');
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/auth/me/telegram/status');
      setConnected(r.data.connected);
      if (r.data.bot_username) setBotUsername(r.data.bot_username);
      if (r.data.connected) { setLinkInfo(null); clearInterval(pollRef.current); }
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => { loadStatus(); return () => clearInterval(pollRef.current); }, [loadStatus]);

  const generateCode = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post('/auth/me/telegram/link-code');
      setLinkInfo(r.data);
      clearInterval(pollRef.current);
      let ticks = 0;
      pollRef.current = setInterval(() => {
        ticks += 1;
        loadStatus();
        if (ticks >= 40) clearInterval(pollRef.current); // ~2 perc, 3mp-enként
      }, 3000);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || t('telegram.generateFailed') });
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.post('/auth/me/telegram/unlink');
      setConnected(false); setLinkInfo(null);
      setMsg({ type: 'success', text: t('telegram.unlinked') });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || t('telegram.unlinkFailed') });
    } finally { setBusy(false); }
  };

  const sendTestMessage = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.post('/auth/me/telegram/test');
      setMsg({ type: 'success', text: t('telegram.testSent') });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || t('telegram.testFailed') });
    } finally { setBusy(false); }
  };

  return (
    <div className="profile-card">
      <h3 className="card-section-title">📲 {t('telegram.title')}</h3>
      {msg && <div className={msg.type === 'error' ? 'alert-error' : 'alert-success'}>{msg.text}</div>}

      {connected === true && (
        <>
          <p className="config-hint">✅ {t('telegram.connected')}</p>
          <div className="telegram-btn-row">
            <Button variant="solid" disabled={busy} onClick={sendTestMessage}>{t('telegram.sendTest')}</Button>
            <Button variant="ghost" disabled={busy} onClick={unlink}>{t('telegram.unlink')}</Button>
          </div>
        </>
      )}

      {connected === false && (
        <>
          <p className="telegram-intro">{t('telegram.notConnected')}</p>
          <ol className="telegram-setup-steps">
            <li>{t('telegram.setupStep1')}</li>
            <li>
              {t('telegram.setupStep2')}{' '}
              <CopyText value={botUsername ? `@${botUsername}` : ''} title={t('telegram.copyHint')}>
                {botUsername ? `@${botUsername}` : t('telegram.setupBotFallback')}
              </CopyText>
            </li>
            <li>{t('telegram.setupStep3')}</li>
          </ol>

          {!linkInfo && (
            <Button variant="solid" disabled={busy} onClick={generateCode}>{t('telegram.generateCode')}</Button>
          )}

          {linkInfo && (
            <div className="telegram-link-box">
              <p className="telegram-link-lead">{t('telegram.linkLead')}</p>

              <div className="telegram-option">
                <span className="telegram-option-label">{t('telegram.optionA')}</span>
                <a href={linkInfo.deep_link} target="_blank" rel="noreferrer"
                   className="btn btn--solid btn--md telegram-open-btn">
                  {t('telegram.openInTelegram')}
                </a>
              </div>

              <div className="telegram-or"><span>{t('telegram.or')}</span></div>

              <div className="telegram-option">
                <span className="telegram-option-label">{t('telegram.optionB')}</span>
                <CopyText value={`/start ${linkInfo.code}`} title={t('telegram.copyHint')}>
                  <code>/start {linkInfo.code}</code>
                </CopyText>
              </div>

              <p className="telegram-waiting">{t('telegram.waitingConnect')}</p>
              <Button variant="ghost" disabled={busy} onClick={loadStatus}>{t('telegram.refreshStatus')}</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// NOTIFICATION PREFERENCES CARD — saját profil
// A szerep + admin-maszk szerint elérhető eseményekhez kapcsolók.
// Opt-out: a "checked" = engedélyezve; a kikapcsoltak a mute-listába kerülnek.
// ============================================================
const NotificationPrefsCard = ({ t, api }) => {
  const [categories, setCategories] = useState(null);
  const [muted, setMuted] = useState([]);
  const [status, setStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const saveTimeoutRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/auth/me/notification-prefs');
      setCategories(r.data.categories || []);
      setMuted(r.data.muted || []);
    } catch { setCategories([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const key = (ch, ev) => `${ch}:${ev}`;
  const isOn = (ch, ev) => !muted.includes(key(ch, ev));
  const setOn = (ch, ev, on) => {
    const newMuted = on ? muted.filter(k => k !== key(ch, ev)) : [...new Set([...muted, key(ch, ev)])];
    setMuted(newMuted);
    debouncedSave(newMuted);
  };

  // Az elérhető csatornák a katalógusból: ügyfélnek csak email jön vissza,
  // agent/adminnak telegram is → a telegram oszlop csak staffnál jelenik meg.
  const hasTelegram = (categories || []).some(c => c.events.some(e => e.channels.includes('telegram')));
  const activeChannels = hasTelegram ? ['email', 'telegram'] : ['email'];

  const visibleEvents = (cat) => cat.events.filter(e => e.channels.some(ch => activeChannels.includes(ch)));
  const cellsOf = (cat) => cat.events.flatMap(e =>
    e.channels.filter(ch => activeChannels.includes(ch)).map(ch => key(ch, e.key)));
  const catAllOn = (cat) => cellsOf(cat).every(k => !muted.includes(k));
  const toggleCat = (cat, on) => {
    const cells = cellsOf(cat);
    const newMuted = new Set(muted);
    cells.forEach(k => { on ? newMuted.delete(k) : newMuted.add(k); });
    const updated = [...newMuted];
    setMuted(updated);
    debouncedSave(updated);
  };

  const debouncedSave = useCallback((mutedList) => {
    clearTimeout(saveTimeoutRef.current);
    setStatus('saving');
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await api.put('/auth/me/notification-prefs', { muted: mutedList });
        setStatus('saved');
      } catch {
        setStatus('error');
      }
    }, 600);
  }, [api]);

  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

  if (categories === null) return null;
  if (!categories.length) return null;

  return (
    <div className="profile-card">
      <h3 className="card-section-title">🔔 {t('notif.profileTitle')}</h3>
      <p className="notif-profile-hint">{t('notif.profileHint')}</p>

      <div className={`notif-matrix${hasTelegram ? '' : ' notif-matrix--email-only'}`}>
        <div className="notif-matrix-head">
          <span />
          <span className="notif-col">{t('notif.channel.email')}</span>
          {hasTelegram && <span className="notif-col">{t('notif.channel.telegram')}</span>}
        </div>
        {categories.filter(cat => visibleEvents(cat).length > 0).map(cat => (
          <div key={cat.key} className="notif-cat">
            <label className="notif-cat-head">
              <input type="checkbox" checked={catAllOn(cat)} onChange={e => toggleCat(cat, e.target.checked)} />
              <strong>{t(`notif.category.${cat.key}`)}</strong>
            </label>
            {visibleEvents(cat).map(ev => (
              <div key={ev.key} className="notif-row">
                <span className="notif-ev-label">{t(`notif.event.${ev.key}`)}</span>
                <span className="notif-cell">
                  {ev.channels.includes('email') && (
                    <input type="checkbox" checked={isOn('email', ev.key)}
                      onChange={e => setOn('email', ev.key, e.target.checked)} />
                  )}
                </span>
                {hasTelegram && (
                  <span className="notif-cell">
                    {ev.channels.includes('telegram') && (
                      <input type="checkbox" checked={isOn('telegram', ev.key)}
                        onChange={e => setOn('telegram', ev.key, e.target.checked)} />
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <SavedIndicator status={status} />
    </div>
  );
};

// ============================================================
// MAIN PROFILE PAGE
// ============================================================
const ProfilePage = ({ userId: userIdProp, onClose }) => {
  const { id: paramId } = useParams();
  const id = userIdProp != null ? String(userIdProp) : paramId;
  const { user: currentUser, api, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const isDrawer = !!onClose;

  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [editMode, setEditMode]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm]               = useState({});

  const canEdit = isAdmin(currentUser?.role);
  const isOwnProfile = currentUser?.id === Number(id);

  // ---- LOCK ---- (számítás a render előtt, profileUser már betöltött ennél a pontnál)
  // Megjegyzés: a tényleges értéket a return blokkban használjuk, ahol profileUser már !== null

  const handleUnlock = async () => {
    try {
      await api.put(`/admin/users/${id}/unlock`);
      setProfileUser(p => ({ ...p, locked_until: null, failed_attempts: 0 }));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError(e.response?.data?.error || t('admin.unlockFailed'));
    }
  };

  // ---- DELETE + GDPR ----
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${id}`);
      if (onClose) onClose(); else navigate('/admin/users');
    } catch (e) {
      setSaveError(e.response?.data?.error || t('admin.deleteFailed'));
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/admin/users/${id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `user_${id}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  // ---- GDPR self-service (saját profil) ----
  const [selfExporting, setSelfExporting] = useState(false);
  const [showSelfDeleteModal, setShowSelfDeleteModal] = useState(false);
  const [selfDeletePassword, setSelfDeletePassword] = useState('');
  const [selfDeleting, setSelfDeleting] = useState(false);
  const [selfDeleteError, setSelfDeleteError] = useState(null);

  const handleSelfExport = async () => {
    setSelfExporting(true);
    try {
      const res = await api.get('/auth/me/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my_data_export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ } finally {
      setSelfExporting(false);
    }
  };

  const handleSelfDelete = async () => {
    setSelfDeleting(true);
    setSelfDeleteError(null);
    try {
      await api.delete('/auth/me', { data: { password: selfDeletePassword } });
      navigate('/login');
    } catch (e) {
      setSelfDeleteError(e.response?.data?.error || t('profile.selfDeleteFailed'));
    } finally {
      setSelfDeleting(false);
    }
  };

  // ---- PROPERTIES ----
  const [userProperties, setUserProperties] = useState([]);

  useEffect(() => {
    if (!canEdit || !id) return;
    api.get(`/properties?user_id=${id}`)
      .then(r => setUserProperties(r.data || []))
      .catch(() => {});
  }, [api, id, canEdit]);

  // ---- SESSIONS ----
  const [sessions, setSessions]       = useState([]);
  const [sessionsLoading, setSessLoad] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const fetchSessions = useCallback(async () => {
    setSessLoad(true);
    try {
      const r = await api.get(`/admin/users/${id}/sessions`);
      setSessions(r.data.sessions);
    } catch { /* silent */ } finally {
      setSessLoad(false);
    }
  }, [api, id]);

  const handleRevokeSession = async (sessionId) => {
    try {
      await api.delete(`/admin/sessions/${sessionId}`);
      setSessions(s => s.filter(x => x.id !== sessionId));
    } catch { /* silent */ }
  };

  const handleRevokeAll = async () => {
    try {
      await api.delete(`/admin/users/${id}/sessions`);
      setSessions([]);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/users/${id}`)
      .then(r => {
        setProfileUser(r.data.user);
        setForm({
          first_name: r.data.user.first_name ?? '',
          last_name:  r.data.user.last_name  ?? '',
          username:   r.data.user.username   ?? '',
          email:      r.data.user.email      ?? '',
          role:       r.data.user.role,
          is_active:  r.data.user.is_active,
        });
      })
      .catch(err => {
        if (err.response?.status === 403) {
          if (onClose) onClose(); else navigate('/dashboard', { replace: true });
        } else {
          setError(t('common.error'));
        }
      })
      .finally(() => setLoading(false));
  }, [id, api, t, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await api.put(`/admin/users/${id}`, form);
      setProfileUser(res.data.user);
      setEditMode(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError(e.response?.data?.error || t('admin.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: profileUser.first_name ?? '',
      last_name:  profileUser.last_name  ?? '',
      username:   profileUser.username   ?? '',
      email:      profileUser.email      ?? '',
      role:       profileUser.role,
      is_active:  profileUser.is_active,
    });
    setEditMode(false);
    setSaveError(null);
  };

  const handleBack = () => {
    if (onClose) { onClose(); return; }
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  if (loading) return <div className="profile-loading">{t('common.loading')}</div>;
  if (error)   return <div className="profile-error">⚠️ {error}</div>;

  const isLocked = profileUser.locked_until && new Date(profileUser.locked_until) > new Date();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
    {!isDrawer && (
      <AppHeader
        siteName="VILLAPRESENCE"
        user={currentUser}
        onLogout={handleLogout}
        onOpenProfile={() => {}} // Own profile, no need to open drawer
        onBack={handleBack}
      />
    )}
    <div className={`profile-page${!isDrawer ? ' profile-page--standalone' : ''}`}>

      {/* FEJLÉC */}
      <div className="profile-header">
        <div className="profile-header-title">
          <h1>
            {profileUser.first_name || profileUser.last_name
              ? `${profileUser.first_name ?? ''} ${profileUser.last_name ?? ''}`.trim()
              : profileUser.username ?? t('auth.username')}
          </h1>
          <RoleBadge role={profileUser.role} />
          {isOwnProfile && <span className="own-badge">{t('admin.you')}</span>}
        </div>
        {canEdit && !editMode && (
          <div className="profile-header-btns">
            <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
              ✏️ {t('admin.edit')}
            </Button>
            {isLocked && (
              <Button variant="outline" size="sm" onClick={handleUnlock} title={t('admin.unlock')}>
                🔓 {t('admin.unlock')}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleExport} title="GDPR export">
              📥 Export
            </Button>
            {!isOwnProfile && (
              <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)}>
                🗑️ {t('admin.delete')}
              </Button>
            )}
          </div>
        )}
        {editMode && (
          <div className="edit-actions">
            <Button variant="solid" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? t('admin.saving') : `💾 ${t('admin.save')}`}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
              {t('admin.cancel')}
            </Button>
          </div>
        )}
      </div>

      {saveSuccess && <div className="profile-success">✅ {t('admin.saved')}</div>}
      {saveError   && <div className="profile-save-error">⚠️ {saveError}</div>}

      {/* KÁRTYA */}
      <div className="profile-card">

        {/* AVATAR + ALAP ADATOK */}
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            {profileUser.avatar_url
              ? <img src={profileUser.avatar_url} alt="avatar" />
              : <span className="avatar-initials">
                  {(profileUser.first_name?.[0] ?? '') + (profileUser.last_name?.[0] ?? '') || '?'}
                </span>
            }
          </div>
          <div className="profile-meta">
            <div className="meta-item">
              <span className="meta-label">{t('admin.status')}</span>
              <span className={`status-badge-inline ${profileUser.is_active ? 'active' : 'inactive'}`}>
                {profileUser.is_active ? `● ${t('admin.active')}` : `● ${t('admin.inactive')}`}
              </span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{t('profile.emailVerified')}</span>
              <span>{profileUser.email_verified ? '✅' : '❌'}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{t('admin.registered')}</span>
              <span>{formatDate(profileUser.created_at)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">{t('admin.lastLogin')}</span>
              <span>{formatDate(profileUser.last_login)}</span>
            </div>
            {(isLocked || (profileUser.failed_attempts > 0)) && (
              <div className="meta-item">
                <span className="meta-label">{t('admin.failedAttempts')}</span>
                <span className={isLocked ? 'lock-status-badge' : ''}>
                  {isLocked && '🔒 '}
                  {profileUser.failed_attempts ?? 0}
                  {isLocked && ` — ${t('admin.lockedUntil')}: ${formatDate(profileUser.locked_until)}`}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="profile-divider" />

        {/* SZERKESZTHETŐ MEZŐK */}
        <div className="profile-fields">

          <div className="fields-row">
            <div className="field-group">
              <label>{t('auth.firstName')}</label>
              {editMode
                ? <input name="first_name" value={form.first_name} onChange={handleChange} />
                : <div className="field-value">{profileUser.first_name || '—'}</div>
              }
            </div>
            <div className="field-group">
              <label>{t('auth.lastName')}</label>
              {editMode
                ? <input name="last_name" value={form.last_name} onChange={handleChange} />
                : <div className="field-value">{profileUser.last_name || '—'}</div>
              }
            </div>
          </div>

          <div className="fields-row">
            <div className="field-group">
              <label>{t('auth.username')}</label>
              {editMode
                ? <input name="username" value={form.username} onChange={handleChange} />
                : <div className="field-value">{profileUser.username || '—'}</div>
              }
            </div>
            <div className="field-group">
              <label>{t('auth.email')}</label>
              {editMode
                ? <input name="email" type="email" value={form.email} onChange={handleChange} />
                : <div className="field-value">{profileUser.email}</div>
              }
            </div>
          </div>

          {canEdit && (
            <div className="fields-row">
              <div className="field-group">
                <label>{t('admin.role')}</label>
                {editMode
                  ? (
                    <select name="role" value={form.role} onChange={handleChange}>
                      {currentUser.role === ROLES.SUPERADMIN && (
                        <option value={0}>Superadmin</option>
                      )}
                      <option value={1}>Admin</option>
                      <option value={2}>User</option>
                    </select>
                  )
                  : <RoleBadge role={profileUser.role} />
                }
              </div>
              <div className="field-group">
                <label>{t('admin.accountStatus')}</label>
                {editMode
                  ? (
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={form.is_active}
                        onChange={handleChange}
                      />
                      <span>{form.is_active ? t('admin.active') : t('admin.inactive')}</span>
                    </label>
                  )
                  : (
                    <span className={`status-badge-inline ${profileUser.is_active ? 'active' : 'inactive'}`}>
                      {profileUser.is_active ? `● ${t('admin.active')}` : `● ${t('admin.inactive')}`}
                    </span>
                  )
                }
              </div>
            </div>
          )}

          {canEdit && userProperties.length > 0 && (
            <div className="profile-properties-section">
              <div className="profile-divider" style={{ margin: '1rem 0 0.75rem' }} />
              <label className="profile-properties-label">🏠 {t('admin.properties') || 'Properties'}</label>
              <div className="profile-properties-list">
                {userProperties.map(p => (
                  <Link
                    key={p.id}
                    to={`/admin/properties?edit=${p.id}`}
                    className="profile-property-link"
                  >
                    <span className="prop-name">{p.name}</span>
                    {p.address_city && (
                      <span className="prop-city">{p.address_city}</span>
                    )}
                    <span className={`prop-status ${p.is_active ? 'active' : 'inactive'}`}>
                      {p.is_active ? '●' : '○'}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* JELSZÓCSERE KÁRTYA */}
      <PasswordChangeCard
        userId={id}
        isSelf={isOwnProfile}
        isAdminUser={canEdit}
        t={t}
        api={api}
      />

      {/* PREFERÁLT NYELV — csak saját profilon */}
      {isOwnProfile && (
        <>
          <ThemeCard t={t} api={api} initial={profileUser.preferred_theme} />
          <PreferredLanguageCard t={t} api={api} initial={profileUser.preferred_language} />
          {isAgent(profileUser.role) && (
            <CaseTranslationLangsCard t={t} api={api} initial={profileUser.case_translation_langs} />
          )}
        </>
      )}

      {/* 2FA KÁRTYA — csak saját profilon */}
      {isOwnProfile && <TwoFactorCard t={t} api={api} />}

      {/* TELEGRAM KÁRTYA — csak saját profilon, staffnak */}
      {isOwnProfile && isAgent(profileUser.role) && <TelegramCard t={t} api={api} />}

      {/* ÉRTESÍTÉSI PREFERENCIÁK — csak saját profilon */}
      {isOwnProfile && <NotificationPrefsCard t={t} api={api} />}

      {/* GDPR SELF-SERVICE KÁRTYA — csak saját profilon */}
      {isOwnProfile && (
        <div className="profile-card gdpr-card">
          <h3 className="card-section-title">🔒 {t('profile.gdprTitle')}</h3>
          <div className="gdpr-row">
            <div className="gdpr-info">
              <strong>{t('profile.gdprExportTitle')}</strong>
              <p>{t('profile.gdprExportDesc')}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleSelfExport} disabled={selfExporting}>
              {selfExporting ? '...' : `📥 ${t('profile.gdprExportBtn')}`}
            </Button>
          </div>
          <div className="gdpr-divider" />
          <div className="gdpr-row">
            <div className="gdpr-info">
              <strong>{t('profile.gdprDeleteTitle')}</strong>
              <p>{t('profile.gdprDeleteDesc')}</p>
            </div>
            <Button variant="danger" size="sm" onClick={() => { setSelfDeleteError(null); setSelfDeletePassword(''); setShowSelfDeleteModal(true); }}>
              🗑️ {t('profile.gdprDeleteBtn')}
            </Button>
          </div>
        </div>
      )}

      {/* SESSIONS */}
      <div className="sessions-card">
        <div className="sessions-header" onClick={() => setSessionsOpen(o => !o)} style={{ cursor: 'pointer', marginBottom: sessionsOpen ? '1.25rem' : 0 }}>
          <h2 className="sessions-title">🖥️ {t('profile.sessions')}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {sessionsOpen && sessions.length > 0 && (
              <Button variant="danger" size="sm" onClick={e => { e.stopPropagation(); handleRevokeAll(); }}>
                {t('profile.revokeAllSessions')}
              </Button>
            )}
            <span className="sessions-toggle">{sessionsOpen ? '▲' : '▼'}</span>
          </div>
        </div>

        {sessionsOpen && (
          sessionsLoading ? (
            <p className="sessions-empty">{t('common.loading')}</p>
          ) : sessions.length === 0 ? (
            <p className="sessions-empty">{t('profile.noSessions')}</p>
          ) : (
            <div className="sessions-list">
              {sessions.map(s => (
                <div key={s.id} className="session-item">
                  <div className="session-info">
                    <div className="session-device">
                      🌐 {s.user_agent
                        ? s.user_agent.substring(0, 60) + (s.user_agent.length > 60 ? '…' : '')
                        : t('profile.sessionDevice')}
                    </div>
                    <div className="session-meta">
                      <span>📍 {s.ip_address || '—'}</span>
                      <span>🕐 {formatDate(s.created_at)}</span>
                    </div>
                  </div>
                  <Button variant="danger" size="sm" onClick={() => handleRevokeSession(s.id)}>
                    {t('profile.revokeSession')}
                  </Button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>

    {/* DELETE CONFIRMATION MODAL */}
    {showDeleteModal && (
      <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="modal-box" onClick={e => e.stopPropagation()}>
          <h3>🗑️ {t('admin.deleteUser')}</h3>
          <p>
            {t('admin.confirmDelete')} <strong>{profileUser.email}</strong><br/>
            <small style={{ color: 'var(--text-muted)' }}>{t('admin.softDeleteNote')}</small>
          </p>
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              {t('admin.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? t('admin.delete') + '...' : t('admin.confirmDeleteBtn')}
            </Button>
          </div>
        </div>
      </div>
    )}
    {/* SELF-DELETE MODAL */}
    {showSelfDeleteModal && (
      <div className="modal-overlay" onClick={() => setShowSelfDeleteModal(false)}>
        <div className="modal-box" onClick={e => e.stopPropagation()}>
          <h3>🗑️ {t('profile.gdprDeleteTitle')}</h3>
          <p>{t('profile.gdprDeleteConfirmText')}</p>
          <input
            type="password"
            className="modal-password-input"
            placeholder={t('profile.gdprDeletePasswordPlaceholder')}
            value={selfDeletePassword}
            onChange={e => setSelfDeletePassword(e.target.value)}
            autoFocus
          />
          {selfDeleteError && <p className="modal-error">{selfDeleteError}</p>}
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowSelfDeleteModal(false)} disabled={selfDeleting}>
              {t('admin.cancel')}
            </Button>
            <Button variant="danger" onClick={handleSelfDelete} disabled={selfDeleting || !selfDeletePassword}>
              {selfDeleting ? '...' : t('profile.gdprDeleteConfirmBtn')}
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProfilePage;
